import dayjs from "dayjs";

interface DraftDates {
  start_date: string | null;
  end_date: string | null;
}

interface ExistingSession {
  id?: number | string;
  start_date?: DateValue;
  end_date?: DateValue;
}

type DateValue = string | number | null | undefined;

interface SessionDates {
  start_date?: DateValue;
  end_date?: DateValue;
}

export type SessionTimeStatus = "future" | "in-progress" | "ended";

export type RescheduleStartError = "before-publish" | "in-past";

export const getSessionTimeStatus = (
  session: SessionDates,
  now = dayjs(),
): SessionTimeStatus => {
  if (session.end_date != null && now.isAfter(dayjs(session.end_date))) {
    return "ended";
  }
  if (session.start_date != null && !now.isBefore(dayjs(session.start_date))) {
    return "in-progress";
  }
  return "future";
};

export const getRescheduleMinStart = (publishDate: DateValue, now = dayjs()) =>
  publishDate && dayjs(publishDate).isAfter(now) ? dayjs(publishDate) : now;

export const getRescheduleStartError = (
  newStart: DateValue,
  originalStart: DateValue,
  publishDate: DateValue,
  now = dayjs(),
): RescheduleStartError | null => {
  if (!newStart) return null;
  const start = dayjs(newStart);
  if (originalStart != null && start.isSame(dayjs(originalStart))) return null;
  if (publishDate && start.isBefore(dayjs(publishDate), "day")) {
    return "before-publish";
  }
  if (start.isBefore(now)) return "in-past";
  return null;
};

export const hasSessionOverlap = (
  draft: DraftDates,
  existingSessions: ExistingSession[] | undefined | null,
  editSessionId?: number | string,
): boolean => {
  if (!draft.start_date || !draft.end_date) return false;
  const newStart = dayjs(draft.start_date).valueOf();
  const newEnd = dayjs(draft.end_date).valueOf();

  return (existingSessions || []).some((s) => {
    if (editSessionId != null && String(s?.id) === String(editSessionId)) {
      return false;
    }
    if (s?.start_date == null || s?.end_date == null) return false;
    const start = dayjs(s.start_date).valueOf();
    const end = dayjs(s.end_date).valueOf();
    return newStart < end && start < newEnd;
  });
};

const sameInstant = (
  a: string | number | null | undefined,
  b: string | number | null | undefined,
): boolean => {
  if (a == null || b == null) return a == null && b == null;
  return dayjs(a).valueOf() === dayjs(b).valueOf();
};

export const datesChanged = (
  draft: DraftDates,
  original?: ExistingSession | null,
): boolean => {
  if (!original) return false;
  return (
    !sameInstant(draft.start_date, original.start_date) ||
    !sameInstant(draft.end_date, original.end_date)
  );
};

// Inizio invariato ma già passato e fine modificata: il BE rifiuta (5029),
// per modificare una sessione iniziata serve un nuovo inizio futuro.
export const isRescheduleOfStartedSession = (
  draft: DraftDates,
  original?: ExistingSession | null,
  now = dayjs(),
): boolean => {
  if (!original || !draft.start_date || !draft.end_date) return false;
  if (!sameInstant(draft.start_date, original.start_date)) return false;
  if (sameInstant(draft.end_date, original.end_date)) return false;
  return !dayjs(draft.start_date).isAfter(now);
};

export const totalMinutesWith = (
  draft: DraftDates,
  existingSessions: ExistingSession[] | undefined | null,
  editSessionId?: number | string,
): number => {
  let total = 0;
  for (const s of existingSessions || []) {
    if (editSessionId != null && String(s?.id) === String(editSessionId)) {
      continue;
    }
    if (s?.start_date == null || s?.end_date == null) continue;
    total += Math.abs(dayjs(s.end_date).diff(dayjs(s.start_date), "minute"));
  }
  if (draft.start_date && draft.end_date) {
    total += Math.abs(
      dayjs(draft.end_date).diff(dayjs(draft.start_date), "minute"),
    );
  }
  return total;
};
